export const A11Y_STORAGE_KEY = 'bg_a11y_v1';
export const A11Y_OPEN_EVENT = 'bg:a11y-open';
export const A11Y_CHANGE_EVENT = 'bg:a11y-change';

/** Set true to show Aa control, A11yPanel, skip-links, and /accessibility page. */
export const SHOW_A11Y_UI = false;

export type A11yTextSize = 'default' | 'large' | 'xlarge';

export type A11yPrefs = {
	textSize: A11yTextSize;
	highContrast: boolean;
	reduceMotion: boolean;
	strongFocus: boolean;
	underlineLinks: boolean;
	/** Helps red–green color vision deficiency: less color-only cues. */
	colorBlindAssist: boolean;
};

export const DEFAULT_A11Y_PREFS: A11yPrefs = {
	textSize: 'default',
	highContrast: false,
	reduceMotion: false,
	strongFocus: false,
	underlineLinks: false,
	colorBlindAssist: false
};

function isBrowser(): boolean {
	return typeof window !== 'undefined';
}

function isTextSize(value: unknown): value is A11yTextSize {
	return value === 'default' || value === 'large' || value === 'xlarge';
}

export function readA11y(): A11yPrefs {
	if (!isBrowser()) return { ...DEFAULT_A11Y_PREFS };

	try {
		const raw = window.localStorage.getItem(A11Y_STORAGE_KEY);
		if (!raw) return { ...DEFAULT_A11Y_PREFS };
		const parsed = JSON.parse(raw) as Partial<A11yPrefs>;
		return {
			textSize: isTextSize(parsed.textSize) ? parsed.textSize : 'default',
			highContrast: parsed.highContrast === true,
			reduceMotion: parsed.reduceMotion === true,
			strongFocus: parsed.strongFocus === true,
			underlineLinks: parsed.underlineLinks === true,
			colorBlindAssist: parsed.colorBlindAssist === true
		};
	} catch {
		return { ...DEFAULT_A11Y_PREFS };
	}
}

const TEXT_ZOOM: Record<A11yTextSize, string> = {
	default: '',
	large: '1.15',
	xlarge: '1.3'
};

/** System preference OR on-site Accessibility panel. */
export function isReducedMotionPreferred(): boolean {
	if (!isBrowser()) return false;
	if (document.documentElement.dataset.a11yMotion === 'reduce') return true;
	try {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	} catch {
		return false;
	}
}

/** Apply prefs to <html> immediately (live preview, like system Accessibility). */
export function applyA11y(prefs: A11yPrefs): void {
	if (!isBrowser()) return;

	const root = document.documentElement;
	root.dataset.a11yText = prefs.textSize;
	root.dataset.a11yContrast = prefs.highContrast ? 'on' : 'off';
	root.dataset.a11yMotion = prefs.reduceMotion ? 'reduce' : 'default';
	root.dataset.a11yFocus = prefs.strongFocus ? 'on' : 'off';
	root.dataset.a11yLinks = prefs.underlineLinks ? 'on' : 'off';
	root.dataset.a11yColorblind = prefs.colorBlindAssist ? 'on' : 'off';

	// zoom via inline style — bundlers often strip `zoom` from CSS
	const zoom = TEXT_ZOOM[prefs.textSize];
	if (zoom) {
		root.style.setProperty('zoom', zoom);
	} else {
		root.style.removeProperty('zoom');
	}

	window.dispatchEvent(new CustomEvent(A11Y_CHANGE_EVENT, { detail: prefs }));
}

export function saveA11y(prefs: A11yPrefs): A11yPrefs {
	const next: A11yPrefs = {
		textSize: isTextSize(prefs.textSize) ? prefs.textSize : 'default',
		highContrast: prefs.highContrast === true,
		reduceMotion: prefs.reduceMotion === true,
		strongFocus: prefs.strongFocus === true,
		underlineLinks: prefs.underlineLinks === true,
		colorBlindAssist: prefs.colorBlindAssist === true
	};

	if (isBrowser()) {
		try {
			window.localStorage.setItem(A11Y_STORAGE_KEY, JSON.stringify(next));
		} catch {
			/* ignore quota / private mode */
		}
	}

	applyA11y(next);
	return next;
}

export function resetA11y(): A11yPrefs {
	if (isBrowser()) {
		try {
			window.localStorage.removeItem(A11Y_STORAGE_KEY);
		} catch {
			/* ignore */
		}
	}
	applyA11y(DEFAULT_A11Y_PREFS);
	return { ...DEFAULT_A11Y_PREFS };
}

export function bootstrapA11y(): A11yPrefs {
	const prefs = readA11y();
	applyA11y(prefs);
	return prefs;
}

type A11yOpenHandler = () => void;
let openHandler: A11yOpenHandler | null = null;

export function registerA11yOpenHandler(handler: A11yOpenHandler): () => void {
	openHandler = handler;
	return () => {
		if (openHandler === handler) openHandler = null;
	};
}

export function openA11yPreferences(): void {
	if (!isBrowser() || !SHOW_A11Y_UI) return;
	if (openHandler) {
		openHandler();
		return;
	}
	window.dispatchEvent(new CustomEvent(A11Y_OPEN_EVENT));
}
